import { useState } from "react";
import { Calendar, CircleDot, SlidersHorizontal, Users, X, type LucideIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import {
  FilterOptionRow,
  FilterPanelBody,
  FilterPanelContent,
  FilterPanelEmpty,
  FilterPanelHeader,
  FilterSection,
  FilterSectionNote,
  FilterSections,
} from "@/components/filters/FilterPanel";
import { matchesFilterQuery } from "@/components/filters/matchesFilterQuery";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { EMPTY_SIGNING_FILTER, type SigningFilterValue } from "../status";
import type { SigningItemStatus } from "../types";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";
import { FilterChips } from "@/components/filters/FilterChips";
import { multiValueChip, type FilterChip } from "@/components/filters/filterChip";

type Dimension = "status" | "signer" | "date";

const DIMENSIONS: Dimension[] = ["status", "signer", "date"];

const DIMENSION_ICON: Record<Dimension, LucideIcon> = {
  status: CircleDot,
  signer: Users,
  date: Calendar,
};

const STATUSES: SigningItemStatus[] = [
  "awaiting_you",
  "awaiting_others",
  "finalizing",
  "signed",
  "failed",
  "cancelled",
  "expired",
  // sessionItemStatus's fallback for an unrecognised backend status — must be
  // filterable or those rows become unreachable once any status is selected.
  "unknown",
];

interface SigningFilterControlProps {
  /** The people present in the current list — derived from the rows. */
  signers: Array<{ id: number; name: string }>;
  value: SigningFilterValue;
  onChange: (next: SigningFilterValue) => void;
}

/**
 * Filter for the Signing list, on the shared filter panel (the /documents
 * design) over the three dimensions signing data actually has: status, the
 * person who signed, and the creation date. A chip reopens the panel with its
 * dimension expanded.
 */
export function SigningFilterControl({ signers, value, onChange }: SigningFilterControlProps) {
  const { t } = useLanguage();
  const f = t.signing.filter;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Dimension | null>(null);

  const dimensionLabel = (d: Dimension): string =>
    d === "status" ? f.status : d === "signer" ? f.signedBy : f.date;

  const statusLabel = (status: SigningItemStatus): string =>
    ({
      awaiting_you: t.signing.status.awaitingYou,
      awaiting_others: t.signing.status.awaitingOthers,
      finalizing: t.signing.status.finalizing,
      signed: t.signing.status.signed,
      failed: t.signing.status.failed,
      cancelled: t.signing.status.cancelled,
      expired: t.signing.status.expired,
      unknown: t.signing.status.unknown,
    })[status];

  const toggleStatus = (status: SigningItemStatus) =>
    onChange({
      ...value,
      statuses: value.statuses.includes(status)
        ? value.statuses.filter((s) => s !== status)
        : [...value.statuses, status],
    });

  const toggleSigner = (id: number) =>
    onChange({
      ...value,
      signerUserIds: value.signerUserIds.includes(id)
        ? value.signerUserIds.filter((s) => s !== id)
        : [...value.signerUserIds, id],
    });

  const openAt = (d: Dimension) => {
    setStep(d);
    setOpen(true);
  };

  // A signer not in the loaded list is counted, never shown as a raw id.
  const signerLabels = value.signerUserIds.flatMap((id) => {
    const name = signers.find((s) => s.id === id)?.name;
    return name ? [name] : [];
  });
  const chips: FilterChip<Dimension>[] = [];
  if (value.statuses.length > 0) {
    chips.push(
      multiValueChip("status", f.status, value.statuses.map(statusLabel), () =>
        onChange({ ...value, statuses: [] })
      )
    );
  }
  if (value.signerUserIds.length > 0) {
    chips.push(
      multiValueChip(
        "signer",
        f.signedBy,
        signerLabels,
        () => onChange({ ...value, signerUserIds: [] }),
        { selectedCount: value.signerUserIds.length }
      )
    );
  }
  if (value.from || value.to) {
    chips.push(
      // The chip reads "Date: …", so the date is a one-value multi chip.
      multiValueChip("date", f.date, [`${value.from ?? "…"} – ${value.to ?? "…"}`], () =>
        onChange({ ...value, from: null, to: null })
      )
    );
  }

  const [query, setQuery] = useState("");
  const searching = query.trim().length > 0;
  const activeCount = chips.length;

  const statusRow = (status: SigningItemStatus, inSearch = false) => (
    <FilterOptionRow
      key={`status-${status}`}
      label={statusLabel(status)}
      ariaLabel={`${f.status}: ${statusLabel(status)}`}
      checked={value.statuses.includes(status)}
      onToggle={() => toggleStatus(status)}
      sublabel={inSearch ? f.status : undefined}
    />
  );
  const signerName = (signer: { id: number; name: string }) => signer.name || String(signer.id);
  const signerRow = (signer: { id: number; name: string }, inSearch = false) => (
    <FilterOptionRow
      key={`signer-${signer.id}`}
      label={signerName(signer)}
      ariaLabel={`${f.signedBy}: ${signerName(signer)}`}
      checked={value.signerUserIds.includes(signer.id)}
      onToggle={() => toggleSigner(signer.id)}
      sublabel={inSearch ? f.signedBy : undefined}
    />
  );

  const selectedCount = (d: Dimension): number =>
    d === "status"
      ? value.statuses.length
      : d === "signer"
        ? value.signerUserIds.length
        : // One range, one chip — and one in the header's count.
          Number(Boolean(value.from || value.to));
  const clearDimension = (d: Dimension) =>
    onChange(
      d === "status"
        ? { ...value, statuses: [] }
        : d === "signer"
          ? { ...value, signerUserIds: [] }
          : { ...value, from: null, to: null }
    );

  const sectionBody = (d: Dimension) => {
    if (d === "status") return STATUSES.map((status) => statusRow(status));
    if (d === "signer") {
      return signers.length > 0 ? (
        signers.map((signer) => signerRow(signer))
      ) : (
        <FilterSectionNote>{f.noSigners}</FilterSectionNote>
      );
    }
    return (
      // Native date inputs — a nested DatePicker popover portals to body,
      // which the outer modal popover treats as an outside pointer-down and
      // dismisses on.
      <div className="flex flex-col gap-2 px-3 py-1.5">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {f.from}
          <Input
            type="date"
            className="h-7 px-2 text-xs"
            value={value.from ?? ""}
            onChange={(event) => onChange({ ...value, from: event.target.value || null })}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {f.to}
          <Input
            type="date"
            className="h-7 px-2 text-xs"
            value={value.to ?? ""}
            onChange={(event) => onChange({ ...value, to: event.target.value || null })}
          />
        </label>
      </div>
    );
  };

  const searchResults = () => {
    const rows = [
      ...STATUSES.filter((status) => matchesFilterQuery(statusLabel(status), query)).map((status) =>
        statusRow(status, true)
      ),
      ...signers
        .filter((signer) => matchesFilterQuery(signerName(signer), query))
        .map((signer) => signerRow(signer, true)),
    ];
    // The date range has no options to list, so a match on its name shows the
    // inputs themselves. Only the dimension's name: "From"/"To" would pull
    // the inputs in on almost any one-letter query.
    const dateMatches = matchesFilterQuery(f.date, query);
    if (rows.length === 0 && !dateMatches) {
      return (
        <FilterPanelEmpty>
          {t.common.filterPanel.noMatches.replace("{query}", () => query.trim())}
        </FilterPanelEmpty>
      );
    }
    return (
      <FilterPanelBody className="py-1">
        {rows}
        {dateMatches && (
          <div>
            <div className="flex items-center gap-2 px-3 pt-1.5 text-xs font-medium">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              {f.date}
            </div>
            {sectionBody("date")}
          </div>
        )}
      </FilterPanelBody>
    );
  };

  return (
    <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
      <FilterChips
        chips={chips}
        onOpenDimension={openAt}
        onClearAll={() => onChange(EMPTY_SIGNING_FILTER)}
      />

      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setStep(null);
            setQuery("");
          }
        }}
        modal={true}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="control"
            className="gap-1.5"
            {...pageInfoTarget("signing.filter")}
          >
            <SlidersHorizontal className="opacity-60" />
            {f.label}
          </Button>
        </PopoverTrigger>
        <FilterPanelContent>
          <FilterPanelHeader
            title={t.common.filterPanel.active}
            activeCount={activeCount}
            clearLabel={t.common.filterPanel.clearAll}
            onClearAll={() => onChange(EMPTY_SIGNING_FILTER)}
            search={{
              value: query,
              onChange: setQuery,
              placeholder: t.common.filterPanel.search,
              clearLabel: t.common.filterPanel.clearSearch,
            }}
          />
          {searching ? (
            searchResults()
          ) : (
            <FilterPanelBody>
              <FilterSections value={step} onValueChange={(next) => setStep(next as Dimension)}>
                {DIMENSIONS.map((d) => (
                  <FilterSection
                    key={d}
                    value={d}
                    icon={DIMENSION_ICON[d]}
                    label={dimensionLabel(d)}
                    selectedCount={selectedCount(d)}
                    onClear={() => clearDimension(d)}
                    clearLabel={f.clear}
                  >
                    {sectionBody(d)}
                  </FilterSection>
                ))}
              </FilterSections>
            </FilterPanelBody>
          )}
        </FilterPanelContent>
      </Popover>
    </div>
  );
}
